import { useState, useEffect, useCallback, useRef } from 'react';
import NumberFormat from 'react-number-format';

import { Trash } from 'lucide-react';
import { DefaultTable, Button, DefaultTooltip } from 'twyne-new.ui';

import BulkDropdown from './BulkDropdown';

export default function BulkSubidTable({ data, disabledDropdowns, handleOnClickOption, columnsMap, dropDownOptions, onChangeData, onChangeTableHasError, onTableError }) {
  const [dataPreview, setDataPreview] = useState([]);

  const rowHasErrors = useCallback(row => {
    const mobilePayoutColumn = columnsMap.mobile_payout.label;
    const payoutColumn = columnsMap.payout.label;

    const mobilePayoutValue = row[mobilePayoutColumn];
    const payoutValue = row[payoutColumn];

    const mobilePayloadIsInvalid = !(!isNaN(mobilePayoutValue) && !!mobilePayoutValue)
    const payloadIsInvalid = !(!isNaN(payoutValue) && !!payoutValue)
    const hasErrors = mobilePayloadIsInvalid || payloadIsInvalid
    return hasErrors
  }, [columnsMap])

  const defineErrors = useCallback(data => !!data.find(row => rowHasErrors(row)), [rowHasErrors])

  const onChangeTableHasErrorRef = useRef(onChangeTableHasError)
  useEffect(() => {
    onChangeTableHasErrorRef.current = onChangeTableHasError;
  }, [onChangeTableHasError]);

  useEffect(() => {
    let dataPreview = [];
    if (data.length <= 10) {
      dataPreview = data
    } else {
      for (let index = 0; index < 10; index++) {
        dataPreview.push(data[index]);
      }
    }

    const newDataPreview = dataPreview.map((item, idx) => ({ ...item, id: idx }))
    setDataPreview(newDataPreview)
    const hasErrors = defineErrors(newDataPreview);
    onChangeTableHasErrorRef.current(hasErrors)
    if (hasErrors) {
      onTableError()
    }
  }, [data, defineErrors, onTableError])

  const { subid, payout, mobile_payout } = columnsMap

  const subidMap = { prop: 'subid', label: subid && subid.label || '' };
  const payoutMap = { prop: 'payout', label: payout && payout.label || '' };
  const mobilePayoutMap = { prop: 'mobile payout', label: mobile_payout && mobile_payout.label || '' };

  const handleDelete = row => {
    const dataFiltered = dataPreview.filter(item => JSON.stringify(row) !== JSON.stringify(item))
    onChangeData(dataFiltered)
  }

  const rowStyle = row => {
    if (!columnsMap || !columnsMap.mobile_payout || !row) {
      return null
    }

    return rowHasErrors(row) ? 'twy-subtle-danger' : null
  }

  const columns = [{
    dataField: columnsMap?.subid?.label,
    headerClassName: 'px-0',
    text: () => <BulkDropdown
      name="subid"
      disabled={disabledDropdowns}
      options={dropDownOptions.options}
      headerTitle={subidMap}
      onClick={handleOnClickOption}
      infoTip="Upload CSV file and map subid column"
      infoTipId="column-select-subid" />,
  }, {
    dataField: columnsMap?.payout?.label,
    headerClassName: 'px-0',
    text: () => <BulkDropdown
      name="payout"
      disabled={disabledDropdowns}
      options={dropDownOptions.numberOptions}
      headerTitle={payoutMap}
      onClick={handleOnClickOption}
      infoTip="Upload CSV file and map payout column"
      infoTipId="column-select-payout" />,
    formatter: cell => <TableNumberFormat cell={cell} />,
  }, {
    dataField: columnsMap?.mobile_payout?.label,
    headerClassName: 'px-0',
    text: () => <BulkDropdown
      name="mobile_payout"
      disabled={disabledDropdowns}
      options={dropDownOptions.numberOptions}
      headerTitle={mobilePayoutMap}
      onClick={handleOnClickOption}
      infoTip="Upload CSV file and map mobile payout column"
    />,
    formatter: cell => <TableNumberFormat cell={cell} />,
  }, {
    dataField: '',
    text: '',
    formatter: (_, row) => <DefaultTooltip content="Delete" triggerProps={{ asChild: true }}>
      <Button color="danger" variant="light" onClick={() => handleDelete(row)}>
        <Trash size={16} />
      </Button>
    </DefaultTooltip>,
    className: 'w-10',
  }]

  return <DefaultTable
    data={dataPreview}
    columns={columns}
    rowClassName={rowStyle}
  />
}

function TableNumberFormat({ cell }) {
  if (!cell) {
    return null;
  }
  return <NumberFormat value={cell} displayType="text" thousandSeparator={true} prefix="$ " />
}

